import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCalendarPlus, FaArrowLeft, FaClock, FaUser, FaBook, FaVideo, FaComment } from 'react-icons/fa';
import api from '../../utils/api';
import { useToast } from '../../hooks/useToast';
import LoadingSpinner from '../common/LoadingSpinner';

/**
 * Session creation component for mentors
 * Allows scheduling new mentoring sessions
 */
const SessionCreation = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(false);
  const [studentsLoading, setStudentsLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    studentId: '',
    sessionDate: '',
    timeSlot: '',
    sessionType: 'video_call',
    topics: '',
    notes: '',
    duration: 60 // Default duration in minutes
  });

  // Fetch assigned students when component mounts
  useEffect(() => {
    fetchAssignedStudents();
  }, []);

  // Fetch available time slots when date changes
  useEffect(() => {
    if (formData.sessionDate) {
      fetchAvailableTimeSlots(formData.sessionDate);
    }
  }, [formData.sessionDate]);

  // Fetch assigned students from API
  const fetchAssignedStudents = async () => {
    try {
      setStudentsLoading(true);
      const response = await api.get('/mentors/children');
      setStudents(response.data.data || []);
    } catch (error) {
      console.error('Error fetching students:', error);
      showError('Failed to load students');
    } finally {
      setStudentsLoading(false);
    }
  };

  // Fetch available time slots for selected date
  const fetchAvailableTimeSlots = async (date) => {
    try {
      setSlotsLoading(true);
      const response = await api.get(`/mentors/available-slots?date=${date}`);
      setAvailableSlots(response.data.data || []);
    } catch (error) {
      console.error('Error fetching time slots:', error);
      showError('Failed to load available time slots');
    } finally {
      setSlotsLoading(false);
    }
  };

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Form validation
  const validateForm = () => {
    if (!formData.studentId) {
      showError('Please select a student');
      return false;
    }
    if (!formData.sessionDate) {
      showError('Please select a date');
      return false;
    }
    if (!formData.timeSlot) {
      showError('Please select a time slot');
      return false;
    }
    if (!formData.topics) {
      showError('Please specify session topics');
      return false;
    }
    return true;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    try {
      setLoading(true);
      
      await api.post('/mentors/sessions', formData);
      
      showSuccess('Session scheduled successfully');
      navigate('/mentor/sessions');
    } catch (error) {
      console.error('Error scheduling session:', error);
      showError(error.response?.data?.message || 'Error scheduling session');
    } finally {
      setLoading(false);
    }
  };

  // Get minimum allowed date (today)
  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // Format time for display
  const formatTime = (timeString) => {
    const options = { hour: '2-digit', minute: '2-digit' };
    return new Date(`1970-01-01T${timeString}`).toLocaleTimeString(undefined, options);
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="mb-6">
        <button 
          onClick={() => navigate('/mentor/sessions')}
          className="flex items-center text-primary hover:text-primary-dark"
        >
          <FaArrowLeft className="mr-2" /> Back to Sessions
        </button>
      </div>
      
      <div className="bg-white rounded-lg shadow-soft p-6">
        <h1 className="text-2xl font-bold flex items-center mb-6">
          <FaCalendarPlus className="mr-2 text-primary" /> Schedule New Session
        </h1>
        
        {loading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner size="lg" />
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="studentId" className="block text-sm font-medium text-gray-700 mb-1">
                  Student <span className="text-red-500">*</span>
                </label>
                {studentsLoading ? (
                  <div className="py-2">
                    <LoadingSpinner size="sm" />
                  </div>
                ) : (
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaUser className="text-gray-400" />
                    </div>
                    <select
                      id="studentId"
                      name="studentId"
                      className="form-input pl-10"
                      value={formData.studentId}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select a student</option>
                      {students.map(student => (
                        <option key={student._id} value={student._id}>
                          {student.firstName} {student.lastName}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              
              <div>
                <label htmlFor="sessionType" className="block text-sm font-medium text-gray-700 mb-1">
                  Session Type <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaVideo className="text-gray-400" />
                  </div>
                  <select
                    id="sessionType"
                    name="sessionType"
                    className="form-input pl-10"
                    value={formData.sessionType}
                    onChange={handleChange}
                    required
                  >
                    <option value="video_call">Video Call</option>
                    <option value="in_person">In Person</option>
                    <option value="phone_call">Phone Call</option>
                    <option value="chat">Chat</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label htmlFor="sessionDate" className="block text-sm font-medium text-gray-700 mb-1">
                  Date <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaCalendarPlus className="text-gray-400" />
                  </div>
                  <input
                    type="date"
                    id="sessionDate"
                    name="sessionDate"
                    className="form-input pl-10"
                    min={getMinDate()}
                    value={formData.sessionDate}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="timeSlot" className="block text-sm font-medium text-gray-700 mb-1">
                  Time Slot <span className="text-red-500">*</span>
                </label>
                {slotsLoading ? (
                  <div className="py-2">
                    <LoadingSpinner size="sm" />
                  </div>
                ) : (
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaClock className="text-gray-400" />
                    </div>
                    <select
                      id="timeSlot"
                      name="timeSlot"
                      className="form-input pl-10"
                      value={formData.timeSlot}
                      onChange={handleChange}
                      required
                      disabled={!formData.sessionDate || availableSlots.length === 0}
                    >
                      <option value="">
                        {!formData.sessionDate 
                          ? 'Select a date first' 
                          : availableSlots.length === 0 
                            ? 'No available slots' 
                            : 'Select a time slot'}
                      </option>
                      {availableSlots.map(slot => (
                        <option key={slot.value} value={slot.value}>
                          {formatTime(slot.value)} - {slot.duration || formData.duration} min
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              
              <div>
                <label htmlFor="duration" className="block text-sm font-medium text-gray-700 mb-1">
                  Duration (minutes) <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaClock className="text-gray-400" />
                  </div>
                  <select
                    id="duration"
                    name="duration"
                    className="form-input pl-10"
                    value={formData.duration}
                    onChange={handleChange}
                    required
                  >
                    <option value="30">30 minutes</option>
                    <option value="45">45 minutes</option>
                    <option value="60">60 minutes</option>
                    <option value="90">90 minutes</option>
                    <option value="120">120 minutes</option>
                  </select>
                </div>
              </div>
              
              <div className="md:col-span-2">
                <label htmlFor="topics" className="block text-sm font-medium text-gray-700 mb-1">
                  Session Topics <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaBook className="text-gray-400" />
                  </div>
                  <input
                    type="text"
                    id="topics"
                    name="topics"
                    className="form-input pl-10"
                    placeholder="E.g. Math homework, Science project, Reading practice"
                    value={formData.topics}
                    onChange={handleChange}
                    required
                  />
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Separate topics with commas
                </p>
              </div>
              
              <div className="md:col-span-2">
                <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
                  Additional Notes
                </label>
                <div className="relative">
                  <div className="absolute top-3 left-3 pointer-events-none">
                    <FaComment className="text-gray-400" />
                  </div>
                  <textarea
                    id="notes"
                    name="notes"
                    rows="4"
                    className="form-textarea pl-10 w-full"
                    placeholder="Any additional details about the session..."
                    value={formData.notes}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>
            
            <div className="mt-8 flex justify-end">
              <button
                type="button"
                onClick={() => navigate('/mentor/sessions')}
                className="btn-secondary mr-4"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={loading}
              >
                {loading ? <LoadingSpinner size="sm" color="white" /> : 'Schedule Session'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default SessionCreation; 